import { useCallback, useRef, useState } from 'react';
import { IconStatusCorrect, useToast } from '@adc/parallax-component-library';

import { useDepartmentDetails } from '~/api/query/departmentDetails';
import { useDepartmentSettingsUpdateMutation } from '~/api/query/departmentSettings';
import {
  DepartmentGeneralSettingsForm,
  type DepartmentGeneralSettingsFormRef,
  type DepartmentGeneralSettingsFormValues,
} from '~/components/forms/department/DeparmentGeneralSettingsForm';
import { Modal } from '~/components/Modal';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';
import type { DateFormatValues } from '~/utils/dateFormats';

type DepartmentDetailsFormModalProps = {
  testID: string;
  departmentId: string;
};

export const DepartmentGeneralSettingsFormModal = ({
  testID,
  departmentId,
}: DepartmentDetailsFormModalProps) => {
  const [hasFormChanged, setHasFormChanged] = useState(false);

  const { showToast } = useToast();
  const { closeModal } = useModal();

  const formRef = useRef<DepartmentGeneralSettingsFormRef>(null);

  const { data: department, isLoading } = useDepartmentDetails(departmentId);

  const { mutateAsync: updateDepartmentSettings } =
    useDepartmentSettingsUpdateMutation();

  const onFormSubmit = useCallback(
    async (values: DepartmentGeneralSettingsFormValues) => {
      await updateDepartmentSettings({ id: departmentId, ...values });

      closeModal('DepartmentGeneralSettingsForm');

      showToast({
        message: 'Update successful',
        'aria-label': 'Update successful',
        icon: <IconStatusCorrect accentColor="$support.positive.default" />,
      });
    },
    [closeModal, showToast, departmentId, updateDepartmentSettings],
  );

  return (
    <Modal
      testID={testID}
      title={isLoading ? 'Loading' : 'Edit general settings'}
      hasCloseButton
      size="m"
      primaryAction={{
        label: 'Save Changes',
        disabled: !hasFormChanged,
        onPress: () => {
          void formRef.current?.submitForm();
        },
      }}
      secondaryAction={{
        label: 'Cancel',
        onPress: () => {
          closeModal('DepartmentGeneralSettingsForm');
        },
      }}
      onOpenChange={() => {
        closeModal('DepartmentGeneralSettingsForm');
      }}
    >
      <DepartmentGeneralSettingsForm
        ref={formRef}
        testID={buildTestId({
          testID,
          suffix: 'form',
        })}
        hasFormChanged={setHasFormChanged}
        onSubmit={onFormSubmit}
        defaultValues={
          department
            ? {
                dateFormat: department.dateFormat as DateFormatValues,
              }
            : undefined
        }
      />
    </Modal>
  );
};
